import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import {
  PenLine, CheckCircle2, XCircle, Clock, AlertCircle,
  Eye, FileText, Send, Mail, Calendar, User, Info,
  CheckCheck, AlertTriangle, Loader
} from 'lucide-react';

function Semaforo({ estado, size = 'md' }) {
  const config = {
    pendiente:       { color: '#f59e0b', bg: '#fef3c7', texto: '⏳ Pendiente',   icono: Clock },
    firmado:         { color: '#16a34a', bg: '#dcfce7', texto: '✅ Firmado',      icono: CheckCircle2 },
    rechazado:       { color: '#dc2626', bg: '#fee2e2', texto: '❌ Rechazado',    icono: XCircle },
    pendiente_firma: { color: '#f59e0b', bg: '#fef3c7', texto: '⏳ Pdte. firma',  icono: Clock },
    borrador:        { color: '#6b7280', bg: '#f0f1f3', texto: '📝 Borrador',     icono: FileText },
  };
  const c = config[estado] || { color: '#6b7280', bg: '#f0f1f3', texto: estado, icono: FileText };
  const Icon = c.icono;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: c.bg, color: c.color,
      padding: size === 'sm' ? '2px 8px' : '4px 12px',
      borderRadius: 20, fontSize: size === 'sm' ? '0.72rem' : '0.8rem',
      fontWeight: 700, border: `1.5px solid ${c.color}30`
    }}>
      <Icon size={size === 'sm' ? 11 : 13} />
      {c.texto}
    </span>
  );
}

function TiempoEspera({ fecha }) {
  const dias = Math.floor((Date.now() - new Date(fecha)) / 86400000);
  const color = dias > 7 ? '#dc2626' : dias > 3 ? '#f59e0b' : '#16a34a';
  const texto = dias === 0 ? 'Hoy' : dias === 1 ? 'Ayer' : `${dias} días`;
  return <span style={{ color, fontWeight: 700, fontSize: '0.78rem' }}>{texto}</span>;
}

function ModalFirmar({ solicitud, user, onClose, onFirmado }) {
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [confirmacion, setConfirmacion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [firmado, setFirmado] = useState(null);

  const handleFirmar = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post(`/firmas/${solicitud.id}/firmar`, { password, confirmacion });
      setFirmado(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al procesar la firma');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PenLine size={18} color="var(--color-primary)" />
            {firmado ? 'Documento Firmado' : 'Firma Electrónica'}
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {firmado ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
              <h3 style={{ color: 'var(--color-success)', marginBottom: 8 }}>Documento firmado correctamente</h3>
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--color-gray-1)', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{solicitud.pnt_titulo}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-3)', marginTop: 4 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700 }}>{solicitud.pnt_codigo}</span>
                  {' · v'}{solicitud.version}
                </div>
              </div>
              {error && <div className="alert alert-danger"><AlertCircle size={15} /> {error}</div>}
              <div className="form-group">
                <label className="form-label">Confirma tu identidad con tu contraseña</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} className="form-control"
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Tu contraseña de acceso" autoFocus />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <Eye size={16} />
                  </button>
                </div>
              </div>
              <label style={{ display: 'flex', gap: 10, cursor: 'pointer', background: confirmacion ? '#f0fdf4' : 'var(--color-gray-1)', padding: 14, borderRadius: 8, border: `1.5px solid ${confirmacion ? '#86efac' : 'var(--color-gray-3)'}` }}>
                <input type="checkbox" checked={confirmacion} onChange={e => setConfirmacion(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--color-success)' }} />
                <span style={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 }}>
                  He revisado el contenido de este documento, lo considero correcto y autorizo su aprobación.
                  Entiendo que esta firma electrónica tiene validez en el marco del sistema de gestión de LOMHIFAR S.L.
                </span>
              </label>
            </>
          )}
        </div>
        <div className="modal-footer">
          {firmado ? (
            <button className="btn btn-primary" onClick={onFirmado}><CheckCircle2 size={16} /> Cerrar</button>
          ) : (
            <>
              <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-success" onClick={handleFirmar} disabled={loading || !confirmacion || !password}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <PenLine size={16} />}
                Firmar Documento
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalRechazar({ solicitud, onClose, onRechazado }) {
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRechazar = async () => {
    setLoading(true);
    try {
      await api.post(`/firmas/${solicitud.id}/rechazar`, { comentario });
      onRechazado();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <XCircle size={18} color="var(--color-danger)" /> Rechazar documento
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 14, padding: 12, background: 'var(--color-gray-1)', borderRadius: 8 }}>
            <div style={{ fontWeight: 600 }}>{solicitud.pnt_titulo}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-3)' }}>{solicitud.pnt_codigo} · v{solicitud.version}</div>
          </div>
          <div className="form-group">
            <label className="form-label">Motivo del rechazo <span className="required">*</span></label>
            <textarea className="form-control" rows={4}
              placeholder="Explica qué debe corregirse antes de poder firmar el documento..."
              value={comentario} onChange={e => setComentario(e.target.value)} autoFocus />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleRechazar} disabled={loading || !comentario}>
            <XCircle size={16} /> Rechazar documento
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FirmasPage() {
  const { canSign, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [pendientes, setPendientes] = useState([]);
  const [pntsPendientes, setPntsPendientes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pendientes');
  const [modalFirmar, setModalFirmar] = useState(null);
  const [modalRechazar, setModalRechazar] = useState(null);
  const [enviandoResumen, setEnviandoResumen] = useState(false);
  const [msgResumen, setMsgResumen] = useState('');
  const [ultimoEnvio, setUltimoEnvio] = useState(null); // { puede_enviar, ultimo, horas_restantes, min_horas }

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/firmas/pendientes'),
      api.get('/firmas/historial'),
      api.get('/firmas/pnts-pendientes').catch(() => ({ data: [] })),
      api.get('/firmas/ultimo-envio').catch(() => ({ data: { puede_enviar: true, ultimo: null } }))
    ])
      .then(([p, h, pp, ue]) => {
        setPendientes(p.data);
        setHistorial(h.data);
        setPntsPendientes(pp.data || []);
        setUltimoEnvio(ue.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Recalcular el estado del botón cada minuto (para que se actualice solo cuando pasen las 4h)
  useEffect(() => {
    const interval = setInterval(() => {
      api.get('/firmas/ultimo-envio')
        .then(r => setUltimoEnvio(r.data))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Helper: texto del botón según estado
  const textoBotonEnvio = () => {
    if (enviandoResumen) return 'Enviando...';
    const total = pendientes.length + pntsPendientes.length;
    if (ultimoEnvio && !ultimoEnvio.puede_enviar && ultimoEnvio.ultimo) {
      const enviado = new Date(ultimoEnvio.ultimo.created_at + 'Z');
      const minPasados = Math.floor((Date.now() - enviado.getTime()) / 60000);
      const txt = minPasados < 60 ? `hace ${minPasados} min` : `hace ${Math.floor(minPasados / 60)}h ${minPasados % 60}m`;
      return `✓ Ya enviado ${txt} (sin novedades)`;
    }
    return `Enviar solicitud de firma (${total})`;
  };

  const botonDeshabilitado = enviandoResumen || (ultimoEnvio && !ultimoEnvio.puede_enviar);

  const handleEnviarResumen = async () => {
    setEnviandoResumen(true); setMsgResumen('');
    try {
      const r = await api.post('/firmas/enviar-resumen');
      setMsgResumen('✅ ' + r.data.mensaje);
      load();
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.error || 'Error al enviar';
      setMsgResumen((status === 429 ? '⏳ ' : '❌ ') + msg);
      if (status === 429) load(); // refrescar estado del botón
    } finally { setEnviandoResumen(false); }
  };

  const firmados = historial.filter(h => h.estado_solicitud === 'firmado');
  const rechazados = historial.filter(h => h.estado_solicitud === 'rechazado');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PenLine size={24} color="var(--color-primary)" /> Gestión de Firmas
          </h1>
          <p style={{ margin: '4px 0 0' }}>
            {canSign() ? 'Revisa y firma los documentos pendientes.' : 'Gestiona las solicitudes de firma de los procedimientos.'}
          </p>
        </div>
        {user?.rol && user.rol !== 'consulta' && (pendientes.length > 0 || pntsPendientes.length > 0) && (
          <button
            className="btn btn-primary"
            onClick={handleEnviarResumen}
            disabled={botonDeshabilitado}
            title={ultimoEnvio && !ultimoEnvio.puede_enviar ? 'Ya enviaste un email con estos mismos documentos. Añade alguno nuevo para reenviar.' : ''}
            style={ultimoEnvio && !ultimoEnvio.puede_enviar ? { opacity: 0.6, cursor: 'not-allowed', background: '#9ca3af' } : {}}
          >
            {enviandoResumen
              ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Enviando...</>
              : <><Mail size={16} /> {textoBotonEnvio()}</>}
          </button>
        )}
      </div>

      {msgResumen && (
        <div className={`alert ${msgResumen.startsWith('✅') ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: 20 }}>
          {msgResumen}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { icono: Clock,        valor: pendientes.length, label: 'Pendientes', color: '#f59e0b', bg: '#fef3c7', urgente: pendientes.length > 0 },
          { icono: CheckCircle2, valor: firmados.length,   label: 'Firmados',   color: '#16a34a', bg: '#dcfce7' },
          { icono: XCircle,      valor: rechazados.length, label: 'Rechazados', color: '#dc2626', bg: '#fee2e2' },
          { icono: FileText,     valor: pendientes.length + historial.length, label: 'Total', color: '#6b7280', bg: '#f0f1f3' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ border: s.urgente ? `2px solid ${s.color}` : undefined }}>
            <div className="stat-icon" style={{ background: s.bg }}><s.icono size={18} color={s.color} /></div>
            <div className="stat-value" style={{ color: s.urgente ? s.color : undefined }}>{s.valor}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--color-gray-3)' }}>
        {[['pendientes', `⏳ Pendientes (${pendientes.length})`], ['historial', `📋 Historial (${historial.length})`]].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '0.9rem', fontWeight: tab === t ? 600 : 400,
            color: tab === t ? 'var(--color-primary)' : 'var(--color-text-3)',
            borderBottom: tab === t ? '2px solid var(--color-primary)' : '2px solid transparent',
            marginBottom: -2, fontFamily: 'var(--font)'
          }}>{label}</button>
        ))}
      </div>

      {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
      tab === 'pendientes' ? (
        <div className="card">
          {pendientes.length === 0 ? (
            <div className="empty-state"><CheckCircle2 size={48} /><h3>No hay documentos pendientes</h3><p>Todos los documentos están al día ✅</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Estado</th><th>Documento</th><th>Código</th><th>Solicitado por</th>
                    <th>Fecha solicitud</th><th>Espera</th><th>Email</th><th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientes.map(s => (
                    <tr key={s.id}>
                      <td><Semaforo estado="pendiente" size="sm" /></td>
                      <td><div style={{ fontWeight: 600 }}>{s.pnt_titulo}</div><div style={{ fontSize: '0.72rem', color: 'var(--color-text-3)' }}>v{s.version}</div></td>
                      <td><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.82rem' }}>{s.pnt_codigo}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{s.solicitante_nombre} {s.solicitante_apellidos}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--color-text-3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Calendar size={12} />{new Date(s.fecha_solicitud).toLocaleDateString('es-ES')}</div>
                        <div style={{ fontSize: '0.7rem' }}>{new Date(s.fecha_solicitud).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td><TiempoEspera fecha={s.fecha_solicitud} /></td>
                      <td style={{ textAlign: 'center' }}>
                        {s.notificado_email ? <span title="Email enviado" style={{ color: 'var(--color-success)' }}>✉️</span> : <span style={{ color: 'var(--color-text-3)', fontSize: '0.7rem' }}>—</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => {
                            const tipo = s.entidad_tipo || 'pnt';
                            if (tipo === 'pnt' || !s.entidad_tipo) navigate(`/pnts/${s.pnt_id}`);
                            else if (tipo === 'informe_mensual') {
                              // Los informes mensuales se ven desde su módulo (Temperatura, Limpieza, Plagas)
                              alert('Este informe mensual se firma directamente desde el panel de Firmas. Pulsa "Firmar" si tienes permisos.');
                            }
                            else if (tipo === 'devolucion') navigate('/devoluciones');
                            else if (tipo === 'no_conformidad') navigate('/no-conformidades');
                            else if (tipo === 'proveedor') navigate('/proveedores');
                            else if (tipo === 'formacion') navigate('/formacion');
                            else alert('Tipo de documento desconocido: ' + tipo);
                          }}><Eye size={13} /> Ver</button>
                          {canSign() ? (
                            <>
                              <button className="btn btn-success btn-sm" onClick={() => setModalFirmar(s)}><PenLine size={13} /> Firmar</button>
                              <button className="btn btn-outline btn-sm" style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={() => setModalRechazar(s)}><XCircle size={13} /> Rechazar</button>
                            </>
                          ) : (
                            <span style={{ fontSize: '0.72rem', color: 'var(--color-text-3)' }}>Esperando firma</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          {historial.length === 0 ? (
            <div className="empty-state"><PenLine size={48} /><h3>Sin historial</h3><p>Aún no hay documentos firmados o rechazados</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Estado</th><th>Documento</th><th>Código</th>
                    <th>Solicitado por</th><th>Fecha solicitud</th>
                    <th>Firmante</th><th>Fecha firma</th><th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h, i) => (
                    <tr key={i}>
                      <td><Semaforo estado={h.estado_solicitud} size="sm" /></td>
                      <td><div style={{ fontWeight: 500 }}>{h.pnt_titulo}</div><div style={{ fontSize: '0.72rem', color: 'var(--color-text-3)' }}>v{h.version}</div></td>
                      <td><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.82rem' }}>{h.pnt_codigo}</span></td>
                      <td style={{ fontSize: '0.78rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><User size={12} color="var(--color-text-3)" />{h.solicitante_nombre} {h.solicitante_apellidos}</div>
                      </td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--color-text-3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Calendar size={12} />{new Date(h.fecha_solicitud).toLocaleDateString('es-ES')}</div>
                      </td>
                      <td style={{ fontSize: '0.78rem' }}>
                        {h.nombre_firmante ? <><strong>{h.nombre_firmante}</strong><div style={{ fontSize: '0.7rem', color: 'var(--color-text-3)' }}>{h.cargo}</div></> : <span style={{ color: 'var(--color-text-3)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.78rem' }}>
                        {h.fecha_firma ? (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: h.estado_solicitud === 'firmado' ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 700 }}>
                              <Calendar size={12} />{new Date(h.fecha_firma).toLocaleDateString('es-ES')}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--color-text-3)' }}>{new Date(h.fecha_firma).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        ) : <span style={{ color: 'var(--color-text-3)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--color-danger)', maxWidth: 180 }}>{h.comentario_rechazo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modalFirmar && <ModalFirmar solicitud={modalFirmar} user={user} onClose={() => setModalFirmar(null)} onFirmado={() => { setModalFirmar(null); load(); }} />}
      {modalRechazar && <ModalRechazar solicitud={modalRechazar} onClose={() => setModalRechazar(null)} onRechazado={() => { setModalRechazar(null); load(); }} />}
    </div>
  );
}
